import React, { useState, useEffect } from 'react';
import { ClockIcon, CursorArrowRaysIcon } from '@heroicons/react/24/outline';
import Button from '../cosmetics/Button';

function MetricInput({ label, name, value, noData, onInputChange, onCheckboxChange, onClickHelp, helperInputText, unit, isCurrency }) {
  // State to track if the input value is valid
  const [isValid, setIsValid] = useState(true);

  // Validate the input value
  useEffect(() => {
    setIsValid(/^-?\d*\.?\d*$/.test(value)); // Regex to allow only numeric values (including decimal)
  }, [value]);

  // Define inline styles for the gradient background
  const gradientBackground = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(169, 168, 243, 0.5))',
  };

  // Function to render the unit addon
  const renderUnitAddon = (position) => {
    const content = isCurrency ? '$' : unit;
    return (
      <span className={`inline-flex items-center border border-gray-300 px-3 text-gray-500 text-sm ${position === 'left' ? 'rounded-l-md border-r-0' : 'rounded-r-md border-l-0'}`}>
        {content}
      </span>
    );
  };

  return (
    <div className="relative bg-white p-2.5 rounded-lg shadow-md" style={gradientBackground}>
      <div className="flex justify-between items-start gap-2">
        <div className="min-h-[2.5rem] flex items-start">
          <h3 className="text-base font-semibold text-gray-700 leading-tight">{label}</h3>
        </div>
        <div className="text-[10px] text-gray-400 flex items-center shrink-0">
          Most recent
          <ClockIcon className="w-2.5 h-2.5 ml-0.5" />
        </div>
      </div>

      <div className="space-y-2 mt-2">
        <div className="relative">
          <label htmlFor={name} className="block text-xs font-small text-gray-700 mb-1">
            {helperInputText}
          </label>
          <div className="flex rounded-md shadow-sm">
            {isCurrency || ['Days', 'Months'].includes(unit) ? (
              <>
                {isCurrency && renderUnitAddon('left')}
                <input
                  type="text"
                  name={name}
                  id={name}
                  value={value}
                  onChange={(e) => onInputChange(name, e.target.value)}
                  disabled={noData}
                  className={`block flex-1 min-w-0 border-gray-300 p-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-sm leading-6 ${isCurrency ? 'rounded-r-md' : 'rounded-l-md'}`}
                  placeholder="0.00"
                />
                {!isCurrency && renderUnitAddon('right')}
              </>
            ) : (
              <>
                {renderUnitAddon('left')}
                <input
                  type="text"
                  name={name}
                  id={name}
                  value={value}
                  onChange={(e) => onInputChange(name, e.target.value)}
                  disabled={noData}
                  className="block flex-1 min-w-0 rounded-none rounded-r-md border-gray-300 p-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-sm leading-6"
                  placeholder="0.00"
                />
              </>
            )}
          </div>
          {!isValid && (
            <div className="flex items-center mt-1">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
              </span>
              <p className="text-xs text-indigo-600 pl-1.5">
                Please enter a number (no commas).
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              name={name}
              id={`no-data-${name}`}
              checked={noData}
              onChange={onCheckboxChange}
              className="h-3 w-3 rounded text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor={`no-data-${name}`} className="ml-1.5 text-xs text-gray-600">We don't track</label>
          </div>
          <button
            type="button"
            onClick={onClickHelp}
            className="text-[10px] text-indigo-600 hover:text-indigo-700 flex items-center"
          >
            <CursorArrowRaysIcon className="w-2.5 h-2.5 mr-0.5" />
            How to calculate
          </button>
        </div>
      </div>
    </div>
  );
}

export default MetricInput;
